import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, map, mergeMap } from 'rxjs';
import { JsonConvert } from 'json2typescript';

import { environment } from '../../../environment/environment';
import { keycloakLogout } from '../../../auth/keycloak.config';
import { Paging } from '../../../model/paging/paging';
import { Project, ProjectCreateResponse, ProjectData, ProjectDataArray } from '../../../model/project/project';
import { HttpUtilsService } from '../../commons/http-uilis.service';

@Injectable({
  providedIn: 'root'
})
export class ProjectService {
  private _url: string = `${environment.baseAPIURL}`;

  constructor(private _http: HttpClient, private _httpUtils: HttpUtilsService) {}

  GETPaginatedProjects(paging: Paging, name?: string): Observable<Project> | any {
    try {
      return this._httpUtils.getHTTPHeadersAsync().pipe(
        mergeMap(httpHeaders => {
          return this._http.get(`${this._url}/projects${this.buildQueryString(paging, name)}`, { headers: httpHeaders, withCredentials: true }).pipe(
            map((res: any) => {
              const jsonConvert: JsonConvert = new JsonConvert();
              const response: ProjectDataArray = jsonConvert.deserializeObject(res, ProjectDataArray);
              if (response.success) {
                return response;
              } else {
                this._httpUtils.handleError(response);
                return;
              }
            }, catchError(err => {
              if (err.status === 401) {
                keycloakLogout({ redirectUri: window.location.origin });
              }
              throw err;
            }))
          );
        }),
        catchError(err => {
          throw err;
        })
      );
    } catch (error) {
      throw error;
    }
  }

  GETProjectById(projectId: string): Observable<Project> | any {
    try {
      return this._httpUtils.getHTTPHeadersAsync().pipe(
        mergeMap(httpHeaders => {
          return this._http.get(`${this._url}/projects/${projectId}`, { headers: httpHeaders, withCredentials: true }).pipe(
            map((res: any) => {
              const jsonConvert: JsonConvert = new JsonConvert();
              const response: ProjectData = jsonConvert.deserializeObject(res, ProjectData);
              if (response.success) {
                return response;
              } else {
                this._httpUtils.handleError(response);
                return;
              }
            }, catchError(err => {
              if (err.status === 401) {
                keycloakLogout({ redirectUri: window.location.origin });
              }
              throw err;
            }))
          );
        }),
        catchError(err => {
          throw err;
        })
      );
    } catch (error) {
      throw error;
    }
  }

  POSTProject(body: Project) {
    try {
      return this._httpUtils.getHTTPHeadersAsync().pipe(
        mergeMap(httpHeaders => {
          return this._http.post(`${this._url}/projects`, this.toCreatePayload(body), { headers: httpHeaders, withCredentials: true }).pipe(
            map((res: any) => {
              const jsonConvert: JsonConvert = new JsonConvert();
              return jsonConvert.deserializeObject(res, ProjectCreateResponse);
            }, catchError(err => {
              if (err.status === 401) {
                keycloakLogout({ redirectUri: window.location.origin });
              }
              throw err;
            }))
          );
        }),
        catchError(err => {
          throw err;
        })
      );
    } catch (error) {
      throw error;
    }
  }

  PUTProject(projectId: string, body: Project) {
    try {
      return this._httpUtils.getHTTPHeadersAsync().pipe(
        mergeMap(httpHeaders => {
          return this._http.put(`${this._url}/projects/${projectId}`, this.toUpdatePayload(body), { headers: httpHeaders, withCredentials: true }).pipe(
            map((res: any) => {
              return res;
            }, catchError(err => {
              if (err.status === 401) {
                keycloakLogout({ redirectUri: window.location.origin });
              }
              throw err;
            }))
          );
        }),
        catchError(err => {
          throw err;
        })
      );
    } catch (error) {
      throw error;
    }
  }

  DELETEProject(projectId: string) {
    try {
      return this._httpUtils.getHTTPHeadersAsync().pipe(
        mergeMap(httpHeaders => {
          return this._http.delete(`${this._url}/projects/${projectId}`, { headers: httpHeaders, withCredentials: true }).pipe(
            map((res: any) => {
              return res;
            }, catchError(err => {
              if (err.status === 401) {
                keycloakLogout({ redirectUri: window.location.origin });
              }
              throw err;
            }))
          );
        }),
        catchError(err => {
          throw err;
        })
      );
    } catch (error) {
      throw error;
    }
  }

  private buildQueryString(paging: Paging, name?: string): string {
    const params = new URLSearchParams({
      offset: String((paging.pageIndex - 1) * paging.pageSize),
      limit: String(paging.pageSize)
    });
    if (name?.trim()) {
      params.set('name', name.trim());
    }
    const sort = paging.sort.find((item) => item.value !== null);
    if (sort?.key) {
      params.set('sortBy', sort.key);
    }
    if (sort?.value === 'ascend') {
      params.set('sortOrder', 'asc');
    } else if (sort?.value === 'descend') {
      params.set('sortOrder', 'desc');
    }
    return `?${params.toString()}`;
  }

  private toCreatePayload(body: Project) {
    return {
      name: body.name,
      description: body.description?.trim() || undefined
    };
  }

  private toUpdatePayload(body: Project) {
    return {
      description: body.description?.trim() || ""
    };
  }
}
